/**
 * Усталость от трека: чем недавнее и чаще он играл, тем ниже он падает в
 * подборе. Смысл — всегда слушать новое, без занудных повторов.
 *
 * Чистая математика, без хранилища и DOM: сюда приходит список событий,
 * отсюда выходят числа. Хранение — в lib/plays.ts.
 */

export type PlayKind = "played" | "sampled" | "skipped";

export interface PlayEvent {
  /** id трека: у Jamendo числовой, у архива `ia:…` — оба стабильны */
  id: string;
  /** когда, миллисекунды эпохи */
  at: number;
  kind: PlayKind;
  /**
   * Кто автор. Появилось позже самой истории, поэтому необязательно: записи,
   * сделанные до этого, просто не участвуют в усталости от артиста.
   */
  artist?: string;
}

const DAY = 86_400_000;

/**
 * Полураспад: через столько дней вклад события падает вдвое.
 *
 * Пропуск держится дольше прослушивания. «Уже слышал» устаревает — через
 * месяц хорошую вещь приятно встретить снова; «не понравилось» устаревает
 * медленнее, потому что вкус меняется не так быстро.
 */
const HALF_LIFE: Record<PlayKind, number> = {
  played: 10 * DAY,
  sampled: 10 * DAY,
  skipped: 30 * DAY,
};

/**
 * Пропуск весит больше: это прямой отказ, а не просто факт знакомства.
 * Частичное прослушивание — меньше всех: ты его знаешь, но ничего не решил.
 */
const WEIGHT: Record<PlayKind, number> = { played: 1, sampled: 0.6, skipped: 1.6 };

/** Выше трёх уже неважно, насколько именно надоело. */
export const MAX_FATIGUE = 3;

/**
 * События → усталость по каждому треку.
 *
 * Считается один раз на подбор, а не на каждого кандидата: история общая для
 * всех, и пересчитывать её двести раз незачем.
 */
export function fatigueIndex(events: PlayEvent[], now = Date.now()): Map<string, number> {
  const out = new Map<string, number>();
  for (const e of events) {
    // Событие «из будущего» (переведённые часы) не должно весить больше свежего.
    const age = Math.max(0, now - e.at);
    const decayed = WEIGHT[e.kind] * Math.pow(0.5, age / HALF_LIFE[e.kind]);
    out.set(e.id, Math.min(MAX_FATIGUE, (out.get(e.id) ?? 0) + decayed));
  }
  return out;
}

/**
 * Сколько разных треков уже прозвучало — для строки «услышано N».
 * Считаем только дослушанные до половины: попробовать — не значит услышать.
 */
export function heardCount(events: PlayEvent[]): number {
  return new Set(events.filter((e) => e.kind === "played").map((e) => e.id)).size;
}

/** Планка «прослушал»: половина длины. */
export const PLAYED_SHARE = 0.5;

/** Ниже этого ухода — отказ, а не знакомство. */
export const SKIP_SECONDS = 20;

/**
 * Что случилось с треком: прослушали, попробовали или отвергли.
 *
 * Три исхода, а не два, и это существенно. Планка «прослушал» — половина
 * длины; но всё, что ниже, не может считаться отказом: уйти с шестиминутной
 * вещи на второй минуте — это не «не понравилось», а «достаточно на сейчас».
 * При двух исходах такой трек получал бы вес отказа и уходил из подбора на
 * месяц, хотя ты его слушал.
 *
 * Отказ — только быстрый уход: двадцати секунд хватает понять, что это не то.
 */
export function verdict(playedSec: number, durationSec: number): PlayKind {
  if (durationSec > 0 && playedSec / durationSec >= PLAYED_SHARE) return "played";
  if (playedSec < SKIP_SECONDS) return "skipped";
  return "sampled";
}

/**
 * Насколько усталость от АРТИСТА слабее усталости от самого трека.
 *
 * История знала только id трека — и в каталоге на сотни тысяч вещей такая
 * память не переносилась ни на что: пропустив три вещи подряд, следующий
 * подбор об этом не узнавал, и тот же автор приходил снова.
 *
 * Слабее намеренно: услышать другую вещь того же автора — это не повтор.
 * Это лишь повод при прочих равных предпочесть кого-то ещё.
 */
const ARTIST_SHARE = 0.45;

/** События → усталость по артистам. Записи без имени автора пропускаются. */
export function artistFatigueIndex(events: PlayEvent[], now = Date.now()): Map<string, number> {
  const out = new Map<string, number>();
  for (const e of events) {
    if (!e.artist) continue;
    const age = Math.max(0, now - e.at);
    const decayed = WEIGHT[e.kind] * ARTIST_SHARE * Math.pow(0.5, age / HALF_LIFE[e.kind]);
    out.set(e.artist, Math.min(MAX_FATIGUE, (out.get(e.artist) ?? 0) + decayed));
  }
  return out;
}
